














































































































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import OnlineBookingService, {
  useOnlineBookingService,
} from '@bookings/services/online-booking-service';

import BpcDisciplineSelector from './discipline-selector.vue';
import BpcSlotDurationSelector from './slot-duration-selector.vue';
import BpcDateSelector from './date-selector.vue';
import BpcSlotSelector from './slot-selector.vue';
import BpcBookingDataConfirmationModal from './booking-data-confirmation-modal.vue';
import BpcResourceSelector from './resource-selector.vue';
import {
  BookingSlot,
  DisciplineListItemDTO,
  ResourceListItemDTO,
  SubscriptionOfContactDTO,
} from '@bookings/interfaces/online-bookings';
import { ToggleButton } from 'vue-js-toggle-button';
import QrcodeVue from 'qrcode.vue';

@Component({
  name: 'bpc-online-booking-form',
  components: {
    BpcDisciplineSelector,
    BpcSlotDurationSelector,
    BpcDateSelector,
    BpcSlotSelector,
    BpcResourceSelector,
    BpcBookingDataConfirmationModal,
    ToggleButton,
    QrcodeVue,
  },
})
export default class OnlineBookingForm extends Vue {
  /* Lifecycle hooks */
  srv: OnlineBookingService = null;

  selectedDiscipline: DisciplineListItemDTO = null;
  selectedDuration = null;
  selectedDate = null;
  selectedResourceId = null;
  selectedSlot: BookingSlot = null;
  selectedResource: ResourceListItemDTO = null;
  selectedPaymentMethod: SubscriptionOfContactDTO = null;

  disciplines: DisciplineListItemDTO[] = [];
  resources: ResourceListItemDTO[] = [];
  subscriptionsOfContact: SubscriptionOfContactDTO[] = [];
  slots: BookingSlot[] = [];
  groupByResource = false;
  loadingResources = false;
  showConfirmationModal = false;
  slotsLoaded = false;

  showMobileAppModal = false;

  get allowedSlotDurations() {
    return this.selectedDiscipline?.allowedSlotDurations || [];
  }

  get subscriptionsOfDiscipline() {
    if (this.selectedDiscipline) {
      return (this.subscriptionsOfContact || []).filter(subscription =>
        subscription.disciplines.includes(this.selectedDiscipline.id),
      );
    }
    return this.subscriptionsOfContact;
  }

  get subscriptionsFilteredBySelectedDuration() {
    if (this.selectedDuration) {
      return this.subscriptionsOfDiscipline.filter(subscription => {
        return (
          (subscription.booking_config.custom_allowed_durations &&
            subscription.booking_config.allowed_durations.includes(this.selectedDuration)) ||
          !subscription.booking_config.custom_allowed_durations
        );
      });
    }

    return this.subscriptionsOfDiscipline;
  }

  get subscriptionsSlotDurations(): number[] {
    const durations = (this.subscriptionsOfDiscipline || []).reduce((res, subscription) => {
      if (subscription.booking_config?.custom_allowed_durations) {
        return res.concat(subscription.booking_config.allowed_durations);
      }
      return res;
    }, []);

    return [...new Set([...durations])];
  }

  get mergedSlotDurations(): number[] {
    return [...new Set([...this.allowedSlotDurations, ...this.subscriptionsSlotDurations])];
  }

  get maxTimeBeforeBooking(): number {
    const maxTime = this.selectedDiscipline?.maxTimeBeforeBooking || 0;
    return this.subscriptionsFilteredBySelectedDuration.reduce((result, subscription) => {
      if (subscription.booking_config.custom_max_time_before_booking) {
        // Return the max between
        return Number(subscription.booking_config.max_time_before_booking) > result
          ? Number(subscription.booking_config.max_time_before_booking)
          : result;
      }
      return result;
    }, maxTime);
  }

  get contactId(): number {
    return this.$authorizationService.getContactId();
  }

  get slotsChangeDetectorStr() {
    return [
      this.selectedDiscipline?.id,
      this.selectedDuration,
      this.selectedDate,
      this.contactId,
    ].join('-');
  }

  get resourcesChangeDetectorStr() {
    return [
      this.selectedDiscipline?.id,
      this.selectedDuration,
      this.selectedDate,
      this.contactId,
      this.selectedSlot?.id,
    ].join('-');
  }

  restartBookingForm() {
    this.selectedDiscipline = null;
    this.selectedDuration = null;
    this.selectedDate = null;
    this.selectedResourceId = null;
    this.selectedSlot = null;
    this.selectedResource = null;
    this.selectedPaymentMethod = null;

    this.disciplines = [];
    this.resources = [];
    this.subscriptionsOfContact = [];
    this.slots = [];
    this.groupByResource = false;
    this.loadingResources = false;
    this.showConfirmationModal = false;
    this.slotsLoaded = false;

    this.getOnlineBookingBaseConfig();
  }

  created() {
    this.srv = useOnlineBookingService();
  }
  mounted() {
    this.getOnlineBookingBaseConfig();
  }

  async getOnlineBookingBaseConfig() {
    const res = await this.srv.getOnlineBookingBaseConfig(this.contactId);
    this.disciplines = res.disciplines;
    this.subscriptionsOfContact = res.subscriptionsOfContact;
    this.selectedDiscipline = this.disciplines[0] || null;
  }

  async getAvailableSlotsIfStopChanging() {
    this.slotsLoaded = false;
    this.slots = [];
    const changeDetectorStr = this.slotsChangeDetectorStr;
    setTimeout(() => {
      if (changeDetectorStr === this.slotsChangeDetectorStr) {
        this.getAvailableSlots();
      }
    }, 300);
  }

  async getAvailableSlots() {
    this.slotsLoaded = false;
    if (!this.selectedDiscipline || !this.selectedDate || !this.selectedDuration) {
      this.slots = [];
      return;
    }
    this.slots = await this.srv.getAvailableSlots(
      this.selectedDiscipline.id,
      this.contactId,
      this.selectedDate,
      this.selectedDuration,
    );
    this.slotsLoaded = true;
  }

  async getAvailableResourcesIfStopChanging() {
    this.resources = [];
    if (!this.selectedSlot) {
      return;
    }
    this.loadingResources = true;
    const changeDetectorStr = this.resourcesChangeDetectorStr;
    setTimeout(() => {
      if (changeDetectorStr === this.resourcesChangeDetectorStr) {
        this.getAvailableResources();
      }
    }, 300);
  }

  async getAvailableResources() {
    this.resources = [];
    if (!this.selectedSlot) {
      this.loadingResources = false;
      return;
    }
    this.loadingResources = true;
    const resourceIds = this.selectedSlot.resources;

    this.resources = await this.srv.getAvailableResources(
      this.selectedDiscipline.id,
      this.contactId,
      this.selectedDate,
      this.selectedDuration,
      this.selectedSlot.startTime,
      resourceIds,
    );
    this.loadingResources = false;
  }

  async bookingDataConfirmed() {
    if (!this.selectedResource) {
      return;
    }
    try {
      const res = await this.srv.addBooking({
        disciplineId: this.selectedDiscipline.id,
        resourceId: this.selectedResource.id,
        startTime: this.selectedSlot.startTime,
        endTime: this.selectedSlot.endTime,
        contactId: this.contactId,
        date: this.selectedDate,
        duration: this.selectedDuration,
        subscriptionId: this.selectedPaymentMethod?.subscription_id,
        subscriptionCategoryId: this.selectedPaymentMethod?.subscription_category_id,
        paymentMethodProvider: this.selectedPaymentMethod?.subscription_id
          ? 'subscription'
          : 'stripe_card',

        paymentMethodProviderDetail: this.selectedPaymentMethod?.subscription_id
          ? String(this.selectedPaymentMethod?.subscription_id)
          : 'card',
      });

      if (res.success) {
        this.$bpcMessage.showSuccessMessage({
          text: 'global.generic.successful_action',
        });
        if (!res.payWithSubscriptions) {
          this.$router.push({
            name: this.$clientRouter.getPaymentProcessRouteNameV2(),
          });
        }
      }
    } catch (res) {
      this.$bpcMessage.showErrorMessage({
        text: 'bookings.online_booking.something_went_wrong',
      });
    }

    this.restartBookingForm();
  }

  @Watch('selectedDiscipline')
  onSelectedDisciplineChange() {
    this.selectedDuration = this.allowedSlotDurations[0] || null;
  }

  @Watch('slotsChangeDetectorStr')
  onSlotsChangeDetectorStrChange() {
    this.selectedSlot = null;
    this.getAvailableSlotsIfStopChanging();
  }

  @Watch('resourcesChangeDetectorStr')
  onResourcesChangeDetectorStrChange() {
    this.getAvailableResourcesIfStopChanging();
  }
  // updated () { }
  // destroyed () { }
}
